import React, { useState } from "react";
import {
  Box,
  Typography,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Paper,
  Chip,
} from "@mui/material";
import {
  PlayArrow,
  PhoneAndroid,
  Computer,
  Apple,
  DesktopWindows,
  Terminal,
} from "@mui/icons-material";
import * as SC from "./guidance.style";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`os-tabpanel-${index}`}
      aria-labelledby={`os-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface VideoCardProps {
  title: string;
  description: string;
  videoSrc: string;
  isAvailable: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({
  title,
  description,
  videoSrc,
  isAvailable,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <SC.VideoCard>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        {isAvailable ? (
          <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden" }}>
            {!isPlaying ? (
              <SC.VideoPlaceholder onClick={handlePlay}>
                <PlayArrow sx={{ fontSize: 60, color: "white" }} />
                <Typography
                  variant="h6"
                  sx={{ color: "white", mt: 1, fontWeight: 600 }}
                >
                  پخش ویدیو
                </Typography>
              </SC.VideoPlaceholder>
            ) : (
              <video
                controls
                autoPlay
                style={{ width: "100%", height: "300px", objectFit: "contain" }}
              >
                <source src={videoSrc} type="video/mp4" />
                مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
              </video>
            )}
          </Box>
        ) : (
          <SC.ComingSoonCard>
            <Typography variant="h6" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
              به زودی
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              راهنمای این سیستم عامل در حال آماده‌سازی است
            </Typography>
          </SC.ComingSoonCard>
        )}
      </CardContent>
    </SC.VideoCard>
  );
};

export const GuidanceComponent = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const operatingSystems = [
    {
      id: "ios",
      name: "iOS",
      icon: <Apple />,
      color: "#007AFF",
      videos: [
        {
          title: "راهنمای اتصال Velora - بخش اول",
          description: "نحوه دانلود و نصب فایل پیکربندی Velora",
          videoSrc: "/guidance-videos/ios-crm-setup-part1.mp4",
          isAvailable: true,
        },
        {
          title: "راهنمای اتصال Velora - بخش دوم",
          description: "نحوه اتصال و استفاده از Velora",
          videoSrc: "/guidance-videos/ios-crm-setup-part2.mp4",
          isAvailable: true,
        },
      ],
    },
    {
      id: "android",
      name: "Android",
      icon: <PhoneAndroid />,
      color: "#4CAF50",
      videos: [
        {
          title: "راهنمای اتصال Velora - Android",
          description: "نحوه اتصال Velora در دستگاه‌های اندروید",
          videoSrc: "",
          isAvailable: false,
        },
      ],
    },
    {
      id: "windows",
      name: "Windows",
      icon: <DesktopWindows />,
      color: "#0078D4",
      videos: [
        {
          title: "راهنمای اتصال Velora - Windows",
          description: "نحوه اتصال Velora در سیستم عامل ویندوز",
          videoSrc: "",
          isAvailable: false,
        },
      ],
    },
    {
      id: "macos",
      name: "macOS",
      icon: <Computer />,
      color: "#000000",
      videos: [
        {
          title: "راهنمای اتصال Velora - macOS",
          description: "نحوه اتصال Velora در سیستم عامل مک",
          videoSrc: "",
          isAvailable: false,
        },
      ],
    },
    {
      id: "linux",
      name: "Linux",
      icon: <Terminal />,
      color: "#FCC624",
      videos: [
        {
          title: "راهنمای اتصال Velora - Linux",
          description: "نحوه اتصال Velora در سیستم عامل لینوکس",
          videoSrc: "",
          isAvailable: false,
        },
      ],
    },
  ];

  return (
    <SC.GuidanceContainer>
      <SC.GuidanceHeader>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontWeight: 700, color: "#333" }}
        >
          راهنمای اتصال Velora
        </Typography>
        <Typography variant="body1" color="text.secondary">
          آموزش کامل نحوه اتصال به Velora در سیستم‌عامل‌های مختلف
        </Typography>
      </SC.GuidanceHeader>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minHeight: 60,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              },
            }}
          >
            {operatingSystems.map((os, index) => {
              const hasAvailableVideos = os.videos.some((video) => video.isAvailable);
              const isDisabled = os.id === "linux";
              
              return (
                <Tab
                  key={os.id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {os.icon}
                      {os.name}
                      {hasAvailableVideos && (
                        <Chip
                          label="موجود"
                          size="small"
                          sx={{
                            backgroundColor: "#4CAF50",
                            color: "white",
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      )}
                      {isDisabled && (
                        <Chip
                          label="غیرفعال"
                          size="small"
                          sx={{
                            backgroundColor: "#9E9E9E",
                            color: "white",
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      )}
                    </Box>
                  }
                  disabled={isDisabled}
                  sx={{
                    color: isDisabled ? "#9E9E9E" : os.color,
                    opacity: isDisabled ? 0.6 : 1,
                    "&.Mui-selected": {
                      color: isDisabled ? "#9E9E9E" : os.color,
                      backgroundColor: isDisabled ? "#F5F5F5" : `${os.color}15`,
                    },
                    "&.Mui-disabled": {
                      color: "#9E9E9E",
                    },
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        {operatingSystems.map((os, index) => (
          <TabPanel key={os.id} value={selectedTab} index={index}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: os.color,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                {os.icon}
                راهنمای اتصال Velora در {os.name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                با دنبال کردن مراحل زیر می‌توانید به راحتی به Velora متصل شوید
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {os.videos.map((video, videoIndex) => (
                <Grid item xs={12} md={6} key={videoIndex}>
                  <VideoCard
                    title={video.title}
                    description={video.description}
                    videoSrc={video.videoSrc}
                    isAvailable={video.isAvailable}
                  />
                </Grid>
              ))}
            </Grid>

            {os.videos.some((video) => video.isAvailable) && (
              <Box sx={{ mt: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  نکات مهم:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    قبل از شروع، مطمئن شوید که اشتراک فعالی دارید
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    در صورت بروز مشکل، با پشتیبانی تماس بگیرید
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    برای بهترین عملکرد، از آخرین نسخه سیستم عامل استفاده کنید
                  </Typography>
                </Box>
              </Box>
            )}
          </TabPanel>
        ))}
      </Paper>
    </SC.GuidanceContainer>
  );
};
